'use client'

import React from 'react'

import { useTenant } from '@rewardops/auth'
import { LocalizedLink, useLocale } from '@rewardops/ui'
import type { EarnMessagingBannerContent } from '@rewardops/utils'

import MapleButton from '~/components/maple/components/button'

type BannerLink = {
  name?: string
  to?: string
  isRelativeUrl?: boolean
}

type Props = {
  content: EarnMessagingBannerContent
}

const EarnMessagingBanner: React.FC<Props> = ({ content }) => {
  const tenant = useTenant()
  const { locale } = useLocale()

  if (!tenant?.flags?.retailers) return null

  const {
    bannerTitle,
    bannerBody,
    bannerButton,
    learnMoreLink,
    icon,
    backgroundColor,
  } = content

  const iconUrl = icon?.file?.url

  const topColor = backgroundColor || '#1a1a1a'
  const gradient = `linear-gradient(180deg, ${topColor} 0%, #2a2a2a 40%, #3d3d3d 70%, #4a4a4a 100%)`
  const backgroundStyle = { background: gradient }

  const renderLink = (link: BannerLink, id: string, isButton = false) => {
    if (!link?.name || !link?.to) return null

    if (link?.isRelativeUrl !== false) {
      if (isButton) {
        return (
          <MapleButton
            id={id}
            link={link.to}
            buttonStyle="secondary"
            buttonType="link"
            className="border-2 border-black bg-white px-6 py-2.5 text-base font-bold text-black hover:bg-gray-100"
          >
            {link.name}
          </MapleButton>
        )
      }
      return (
        <LocalizedLink
          href={link.to}
          className="text-sm font-medium underline hover:no-underline"
        >
          {link.name}
        </LocalizedLink>
      )
    }

    const href = link.to.startsWith('/')
      ? `${tenant?.publicUrl}/${locale}/${link.to}`
      : link.to

    if (isButton) {
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center rounded-lg border-2 border-black bg-white px-6 py-2.5 text-base font-bold text-black hover:bg-gray-100"
        >
          {link.name}
        </a>
      )
    }

    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-sm font-medium underline hover:no-underline"
      >
        {link.name}
      </a>
    )
  }

  if (!bannerTitle && !bannerBody) return null

  const iconElement = iconUrl ? (
    <img src={iconUrl} alt="" className="mx-auto h-16 w-16 object-contain" />
  ) : null

  return (
    <div
      className="rounded-lg border border-gray-600 p-6 text-center text-white"
      style={backgroundStyle}
      data-testid="earn-messaging-banner"
    >
      <div className="flex flex-col items-center gap-4">
        {iconElement}
        {bannerTitle && (
          <h3 className="text-xl font-bold leading-tight">{bannerTitle}</h3>
        )}
        {bannerBody && (
          <p className="max-w-md text-sm text-white/90">{bannerBody}</p>
        )}
        <div className="flex flex-col items-center gap-3">
          {bannerButton &&
            renderLink(bannerButton, 'earn-messaging-banner-button', true)}
          {learnMoreLink && (
            <div className="mt-1">
              {renderLink(learnMoreLink, 'earn-messaging-learn-more')}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export { EarnMessagingBanner }
